// ===========================================
// REGISTER PAGE - Production Ready HD Design
// Matches Tripnovac AI UI Pattern with Purple/Lilac Theme
// ===========================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { Mail, Lock, Eye, EyeOff, User, Building2, Globe, Sparkles, Diamond, CheckCircle, Zap, Shield, BarChart3, Gift } from 'lucide-react';
import TurnstileField, { turnstileEnabled } from '../../components/auth/TurnstileField';

// HD Travel Images - Crystal Clear Background Images that rotate
const heroImages = [
  'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1920&q=95&auto=format&fit=crop', // Mountain Lake
  'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1920&q=95&auto=format&fit=crop', // Swiss Alps Lake
  'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1920&q=95&auto=format&fit=crop', // Mountains
  'https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=1920&q=95&auto=format&fit=crop', // Tropical Beach
  'https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=1920&q=95&auto=format&fit=crop', // Santorini Sunset
  'https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=1920&q=95&auto=format&fit=crop', // Venice
  'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1920&q=95&auto=format&fit=crop', // Tokyo Night
  'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=1920&q=95&auto=format&fit=crop', // NYC Skyline
];

// Hero text content that matches images
const heroContent = [
  { title: 'Start Your', subtitle: 'Journey', tagline: 'Join thousands of travel agencies transforming their marketing' },
  { title: 'Build Your', subtitle: 'Empire', tagline: 'Enterprise-grade tools at your fingertips from day one' },
  { title: 'Reach New', subtitle: 'Heights', tagline: 'Unlock powerful automation that scales with your business' },
  { title: 'Create Your', subtitle: 'Legacy', tagline: 'Join the future of travel marketing innovation' },
  { title: 'Amplify Your', subtitle: 'Impact', tagline: 'Connect with travelers across 249 countries and territories (ISO 3166-1)' },
  { title: 'Launch Your', subtitle: 'Success', tagline: 'Get started in minutes with our intuitive platform' },
  { title: 'Discover New', subtitle: 'Possibilities', tagline: 'AI-powered insights that drive real results' },
  { title: 'Shape Your', subtitle: 'Future', tagline: 'The complete toolkit for modern travel professionals' },
];

// Features list
const features = [
  { icon: Zap, text: 'AI-Powered Campaign Automation' },
  { icon: BarChart3, text: 'Real-Time Analytics Dashboard' },
  { icon: Globe, text: '249 ISO countries and territories' },
  { icon: Shield, text: 'Enterprise Security & Compliance' },
];

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { register, isLoading, error, clearError } = useAuthStore();
  const [searchParams] = useSearchParams();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    organizationName: '',
    referralCode: searchParams.get('ref') || '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Rotate background images
  useEffect(() => {
    setCurrentImageIndex(Math.floor(Math.random() * heroImages.length));
    
    const interval = setInterval(() => {
      setImageLoaded(false);
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 20000); // Change every 20 seconds for stability

    return () => clearInterval(interval);
  }, []);

  // Preload next image
  useEffect(() => {
    const nextIndex = (currentImageIndex + 1) % heroImages.length;
    const img = new Image();
    img.src = heroImages[nextIndex];
  }, [currentImageIndex]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setValidationError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setValidationError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (formData.password.length < 8) {
      setValidationError('Password must be at least 8 characters');
      return;
    }

    if (!acceptedTerms) {
      setValidationError('You must accept the Terms of Use (EULA) and Privacy Policy to continue.');
      return;
    }

    if (turnstileEnabled() && !captchaToken) {
      setValidationError('Complete bot protection to create an account.');
      return;
    }

    try {
      await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        organizationName: formData.organizationName,
        referralCode: formData.referralCode || undefined,
        captchaToken: captchaToken || undefined,
      });
      navigate('/dashboard');
    } catch (err) {
      // Error handled by store
    }
  };

  const currentContent = heroContent[currentImageIndex];

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* HD Background Image - Crystal Clear, No Overlay */}
      <div className="absolute inset-0">
        <img
          src={heroImages[currentImageIndex]}
          alt="Travel destination"
          className={`w-full h-full object-cover transition-opacity duration-1000 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
        />
      </div>

      {/* Top Right Badges */}
      <div className="absolute top-6 right-6 z-20 flex items-center gap-3">
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm font-medium">
          <Globe className="w-4 h-4 text-violet-300" />
          <span>249 countries and territories</span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/80 backdrop-blur-md text-white text-sm font-medium">
          <Sparkles className="w-4 h-4" />
          <span>AI-Powered</span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-sm font-medium">
          <Diamond className="w-4 h-4" />
          <span>Premium</span>
        </div>
      </div>

      {/* Top Right Stats */}
      <div className="absolute top-6 right-[420px] z-20 hidden xl:flex items-center gap-8 text-white">
        <div className="text-center">
          <div className="text-2xl font-bold">Free</div>
          <div className="text-xs text-white/70">14-Day Trial</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold">100%</div>
          <div className="text-xs text-white/70">Secure</div>
        </div>
      </div>

      {/* Bottom Left Hero Text & Features */}
      <div className="absolute bottom-12 left-12 z-20 max-w-lg hidden lg:block">
        <h1 className="text-5xl font-bold text-white leading-tight mb-2">
          {currentContent.title}
          <br />
          <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent">
            {currentContent.subtitle}
          </span>
        </h1>
        <p className="text-white/80 text-lg mt-4 mb-8">
          {currentContent.tagline}
        </p>

        {/* Features List */}
        <div className="space-y-3">
          {features.map((feature, index) => (
            <div key={index} className="flex items-center gap-3 text-white/90">
              <div className="w-8 h-8 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center">
                <feature.icon className="w-4 h-4 text-violet-300" />
              </div>
              <span>{feature.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Centered Register Card */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4 py-8">
        <div className="w-full max-w-md">
          {/* Glass Card */}
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/50">
            {/* Logo */}
            <div className="flex justify-center mb-3">
              <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-lg shadow-violet-500/30">
                <img 
                  src="/assets/sales-icon.jpeg" 
                  alt="Tripnovac Sales" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Brand Name - Using Tripnovac Font Style */}
            <div className="text-center mb-4">
              <div className="flex justify-center mb-1">
                <img 
                  src="/assets/tripnovac-nameplate.jpeg" 
                  alt="Tripnovac" 
                  className="h-7 w-auto object-contain"
                />
              </div>
              {/* Sales & Marketing in matching gradient colors but different font */}
              <p className="text-sm font-light tracking-wider bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 bg-clip-text text-transparent">
                Sales & Marketing
              </p>
            </div>

            {/* Create Account Text */}
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-800">Create Your Account</h3>
              <p className="text-gray-500 text-sm">Start your 14-day free trial</p>
            </div>

            {/* Error Alert */}
            {(error || validationError) && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm">
                {error || validationError}
              </div>
            )}

            {/* Register Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    First Name
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <User className="w-4 h-4" />
                    </div>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="John"
                      className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 bg-gray-50/50 focus:bg-white focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 outline-none transition-all text-gray-800 placeholder-gray-400 text-sm"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Last Name
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <User className="w-4 h-4" />
                    </div>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Doe"
                      className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 bg-gray-50/50 focus:bg-white focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 outline-none transition-all text-gray-800 placeholder-gray-400 text-sm"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Organization Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Agency / Company Name
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <Building2 className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    name="organizationName"
                    value={formData.organizationName}
                    onChange={handleChange}
                    placeholder="Wanderlust Travel Agency"
                    className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 bg-gray-50/50 focus:bg-white focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 outline-none transition-all text-gray-800 placeholder-gray-400 text-sm"
                    required
                  />
                </div>
              </div>

              {/* Referral Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Referral code <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <Gift className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    name="referralCode"
                    value={formData.referralCode}
                    onChange={handleChange}
                    placeholder="Partner code"
                    className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 bg-gray-50/50 focus:bg-white focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 outline-none transition-all text-gray-800 placeholder-gray-400 text-sm uppercase"
                  />
                </div>
                <p className="mt-1.5 text-xs text-gray-500">
                  Have a partner code? You&apos;ll get <strong>20% off your first month</strong> when you upgrade.
                </p>
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Work Email
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <Mail className="w-4 h-4" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 bg-gray-50/50 focus:bg-white focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 outline-none transition-all text-gray-800 placeholder-gray-400 text-sm"
                    required
                  />
                </div>
              </div>

              {/* Password Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <Lock className="w-4 h-4" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-200 bg-gray-50/50 focus:bg-white focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 outline-none transition-all text-gray-800 placeholder-gray-400 text-sm"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Confirm
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <Lock className="w-4 h-4" />
                    </div>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-200 bg-gray-50/50 focus:bg-white focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 outline-none transition-all text-gray-800 placeholder-gray-400 text-sm"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Terms — required before signup */}
              <label className="flex items-start gap-3 text-xs text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                  required
                />
                <span>
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline font-medium">
                    Terms of Use (EULA)
                  </a>{' '}
                  and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline font-medium">
                    Privacy Policy
                  </a>
                </span>
              </label>

              <TurnstileField onToken={setCaptchaToken} />

              {/* Sign Up Button */}
              <button
                type="submit"
                disabled={isLoading || !acceptedTerms}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white font-semibold text-base shadow-lg shadow-violet-500/30 hover:shadow-violet-500/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Creating Account...</span>
                  </div>
                ) : (
                  'Start Free Trial'
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-400">or</span>
              </div>
            </div>

            {/* Social Login */}
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => authService.loginWithGoogle()} className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all text-gray-700 font-medium text-sm">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Google</span>
              </button>
              <button onClick={() => authService.loginWithApple()} className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all text-gray-700 font-medium text-sm">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <span>Apple</span>
              </button>
            </div>

            {/* Sign In Link */}
            <p className="text-center text-gray-600 mt-5 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-violet-600 hover:text-violet-700 font-semibold">
                Sign in
              </Link>
            </p>

            {/* Security Badges */}
            <div className="flex items-center justify-center gap-5 mt-5 pt-5 border-t border-gray-100">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                <span>256-bit SSL</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                <span>SOC 2</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                <span>GDPR</span>
              </div>
            </div>

            {/* Copyright */}
            <p className="text-center text-xs text-gray-400 mt-3">
              © 2026 Tripnovac by Alpha Libra LLC
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
